import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Form, Input } from 'antd';
import { useState } from 'react';
import { requestPasswordCaptcha, resetPassword } from '@/features/auth/api/authApi';
import { useCaptchaCountdown } from '@/features/auth/model/useCaptchaCountdown';
import { normalizeApiError } from '@/shared/api/errors';
import { notifyApiSuccess, suppressApiErrorToast } from '@/shared/api/httpClient';
/** 手机号格式，与后端 DTO 校验保持一致。 */
const PHONE_PATTERN = /^1[3-9]\d{9}$/;
/**
 * 渲染找回密码表单：注册手机号 + 短信验证码 + 新密码。
 *
 * 全程免登录，身份由「手机号已注册 + 密码重置验证码」共同证明；
 * 重置成功后不自动登录，交回登录视图让用户用新密码重新登录。
 */
export function ForgotPasswordForm(props) {
    const [form] = Form.useForm();
    const { countdown, startCountdown } = useCaptchaCountdown();
    const [errorText, setErrorText] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [sendingCaptcha, setSendingCaptcha] = useState(false);
    /** 校验手机号后请求发送密码重置验证码，并进入 60 秒冷却。 */
    async function handleSendCaptcha() {
        try {
            await form.validateFields(['phone']);
        }
        catch {
            return;
        }
        setSendingCaptcha(true);
        setErrorText(null);
        try {
            await requestPasswordCaptcha(form.getFieldValue('phone').trim());
            startCountdown();
        }
        catch (error) {
            /** 失败文案就地展示在表单里，同步抑制统一出口，避免同一次失败弹两处。 */
            setErrorText(normalizeApiError(error).message);
            suppressApiErrorToast(error);
        }
        finally {
            setSendingCaptcha(false);
        }
    }
    /** 提交重置请求，成功后交回登录视图由用户用新密码登录。 */
    async function handleFinish(values) {
        setSubmitting(true);
        setErrorText(null);
        try {
            await resetPassword({
                phone: values.phone.trim(),
                captcha: values.captcha,
                password: values.newPassword,
            });
            notifyApiSuccess('密码已重置，请使用新密码登录');
            props.onBackToLogin();
        }
        catch (error) {
            /** 失败文案就地展示在表单里，同步抑制统一出口，避免同一次失败弹两处。 */
            setErrorText(normalizeApiError(error).message);
            suppressApiErrorToast(error);
        }
        finally {
            setSubmitting(false);
        }
    }
    return (_jsxs(Form, { className: "auth-form", form: form, layout: "vertical", requiredMark: false, onFinish: (values) => {
            void handleFinish(values);
        }, children: [_jsx("p", { className: "auth-form__account", children: "\u9A8C\u8BC1\u6CE8\u518C\u624B\u673A\u53F7\u540E\uFF0C\u53EF\u91CD\u7F6E\u8BE5\u8D26\u53F7\u7684\u767B\u5F55\u5BC6\u7801" }), errorText && (_jsx("p", { className: "auth-form__error", role: "alert", children: errorText })), _jsx(Form.Item, { label: "\u624B\u673A\u53F7", name: "phone", rules: [
                    { required: true, message: '请输入手机号' },
                    { pattern: PHONE_PATTERN, message: '手机号格式不正确' },
                ], children: _jsx(Input, { inputMode: "numeric", maxLength: 11, placeholder: "\u8BF7\u8F93\u5165\u6CE8\u518C\u624B\u673A\u53F7", autoComplete: "tel" }) }), _jsxs("div", { className: "auth-form__captcha-row", children: [_jsx(Form.Item, { className: "auth-form__captcha-field", label: "\u9A8C\u8BC1\u7801", name: "captcha", rules: [
                            { required: true, message: '请输入验证码' },
                            { pattern: /^\d{6}$/, message: '验证码为 6 位数字' },
                        ], children: _jsx(Input, { inputMode: "numeric", maxLength: 6, placeholder: "6 \u4F4D\u9A8C\u8BC1\u7801", autoComplete: "one-time-code" }) }), _jsx(Button, { className: "auth-form__captcha-button", type: "default", disabled: countdown > 0, loading: sendingCaptcha, onClick: () => {
                            void handleSendCaptcha();
                        }, children: countdown > 0 ? `${String(countdown)}s 后重发` : '获取验证码' })] }), _jsx(Form.Item, { label: "\u65B0\u5BC6\u7801", name: "newPassword", rules: [
                    { required: true, message: '请输入新密码' },
                    { min: 6, max: 32, message: '密码长度需在 6-32 位之间' },
                ], children: _jsx(Input.Password, { placeholder: "6-32 \u4F4D\u5BC6\u7801", autoComplete: "new-password" }) }), _jsx(Form.Item, { label: "\u786E\u8BA4\u65B0\u5BC6\u7801", name: "confirmPassword", dependencies: ['newPassword'], rules: [
                    { required: true, message: '请再次输入新密码' },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('newPassword') === value)
                                return Promise.resolve();
                            return Promise.reject(new Error('两次输入的密码不一致'));
                        },
                    }),
                ], children: _jsx(Input.Password, { placeholder: "\u8BF7\u518D\u6B21\u8F93\u5165\u65B0\u5BC6\u7801", autoComplete: "new-password" }) }), _jsx(Button, { className: "auth-form__submit", type: "primary", htmlType: "submit", block: true, loading: submitting, children: "\u91CD\u7F6E\u5BC6\u7801" }), _jsx("div", { className: "auth-form__links", children: _jsx("button", { className: "auth-form__link", type: "button", onClick: props.onBackToLogin, children: "\u8FD4\u56DE\u767B\u5F55" }) })] }));
}
